import { EasyAvatar } from "fin-web-components";
import { useEffect, useRef } from "react";
import { PictureOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { Image, Input, Spin, message } from "antd";
import { CustomerServiceChatModel } from "./CustomerServiceChatModel";
import {
  CS_AVATAR_URL,
  CS_HISTORY_RECYCLED_HINT,
  type CsMessage,
} from "./CustomerServiceChatTypes";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { globalModel } from "../../global/GlobalModel";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { renderTextWithLinks } from "../../utils/renderTextWithLinks";
import { CsBroadcastMessageCard } from "./CsBroadcastMessageCard";
import "./CustomerServiceChatStyle.css";

function formatChatTime(sentAt?: string): string {
  if (!sentAt?.trim()) return "";
  const d = new Date(sentAt.replace(/-/g, "/"));
  if (Number.isNaN(d.getTime())) return sentAt;
  const mo = d.getMonth() + 1;
  const day = d.getDate();
  const hm = d.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const y = d.getFullYear();
  const nowY = new Date().getFullYear();
  if (y !== nowY) return `${y}年${mo}月${day}日 ${hm}`;
  return `${mo}月${day}日 ${hm}`;
}

function shouldShowTimeDivider(prev?: CsMessage, curr?: CsMessage): boolean {
  if (!curr?.sentAt) return false;
  if (!prev?.sentAt) return true;
  const a = new Date(prev.sentAt.replace(/-/g, "/")).getTime();
  const b = new Date(curr.sentAt.replace(/-/g, "/")).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return b - a > 5 * 60 * 1000;
}

export const CustomerServiceChatPanel = observer(function CustomerServiceChatPanel(props: {
  model: CustomerServiceChatModel;
}) {
  const { model } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickBottomRef = useRef(true);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void model.bootstrap();
    return () => model.dispose();
  }, [model]);

  useEffect(() => {
    if (!stickBottomRef.current || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [model.messages.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop <= 24 && model.hasOlder && !model.loadingOlder && !model.historyRecycled) {
      void model.loadOlder();
    }
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickBottomRef.current = dist < 80;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void model.send();
    }
  };

  const userName = globalModel.currentUser?.name ?? "我";
  const userAvatar = resolveAppMediaUrl(globalModel.currentUser?.avatarUrl);

  return (
    <div className="lf-cs-chat">
      <div ref={scrollRef} className="lf-cs-chat-scroll" onScroll={onScroll}>
        {model.loadingOlder ? (
          <div className="lf-cs-chat-loading-top">
            <Spin size="small" /> 加载更早消息…
          </div>
        ) : null}
        {model.historyRecycled ? (
          <div className="lf-cs-chat-history-tip">{CS_HISTORY_RECYCLED_HINT}</div>
        ) : null}
        {model.loading && model.messages.length === 0 ? (
          <div className="lf-cs-chat-loading">
            <Spin />
          </div>
        ) : null}
        {model.messages.map((m, idx) => {
          const prev = idx > 0 ? model.messages[idx - 1] : undefined;
          const isSelf = m.senderType === "CUSTOMER";
          const staffName = m.senderName?.trim() || model.displayName;
          const staffAvatar = resolveAppMediaUrl(m.senderAvatarUrl) || undefined;
          return (
            <div key={m.id}>
              {shouldShowTimeDivider(prev, m) ? (
                <div className="lf-cs-chat-time">{formatChatTime(m.sentAt)}</div>
              ) : null}
              <div className={`lf-cs-msg-row${isSelf ? " lf-cs-msg-row--self" : ""}`}>
                <EasyAvatar
                  shape="square"
                  squareRadius={4}
                  size={36}
                  url={isSelf ? userAvatar || undefined : staffAvatar || CS_AVATAR_URL}
                  word={avatarDisplayWord(isSelf ? userName : staffName)}
                  alt=""
                  className="lf-cs-msg-avatar"
                />
                <div className="lf-cs-msg-col">
                  {!isSelf ? (
                    <div className="lf-cs-msg-name">{staffName}</div>
                  ) : null}
                  <div
                    className={`lf-cs-msg-bubble${isSelf ? " lf-cs-msg-bubble--self" : ""}${
                      m.messageType === "BROADCAST" ? " lf-cs-msg-bubble--broadcast" : ""
                    }`}
                  >
                    {m.messageType === "BROADCAST" ? (
                      <CsBroadcastMessageCard content={m.content} />
                    ) : m.messageType === "IMAGE" && m.content?.trim() ? (
                      <Image
                        src={resolveAppMediaUrl(m.content)}
                        alt="图片消息"
                        className="lf-cs-msg-image"
                        preview={{ mask: false, destroyOnHidden: true }}
                      />
                    ) : (
                      renderTextWithLinks(m.content, "lf-cs-msg-link")
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="lf-cs-compose">
        <div className="lf-cs-compose-toolbar">
          <button
            type="button"
            className="lf-cs-compose-tool-btn"
            title="发送图片"
            onClick={() => imageInputRef.current?.click()}
            disabled={model.sending || !model.conversationId}
          >
            <PictureOutlined />
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="lf-cs-compose-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              if (!file.type.startsWith("image/")) {
                message.warning("请选择图片文件");
                return;
              }
              void model.sendImage(file);
            }}
          />
        </div>
        <div className="lf-cs-compose-box">
          <Input.TextArea
            value={model.draft}
            onChange={(e) => model.setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="输入消息，Enter 发送，Shift+Enter 换行"
            disabled={model.sending || !model.conversationId}
            className="lf-cs-compose-input"
            autoSize={false}
            style={{ height: 100, minHeight: 100, maxHeight: 100, resize: "none" }}
          />
          <button
            type="button"
            className={`lf-cs-send-btn${model.canSend ? " lf-cs-send-btn--active" : ""}`}
            disabled={!model.canSend}
            onClick={() => void model.send()}
          >
            发送
          </button>
        </div>
      </div>
    </div>
  );
});
